import moment from 'moment';

// Shape returned by POST /reporting/:reportKey/chart/:chartKey — positional rows.
export interface ReportChartData {
  columns: { id: string; name: string }[];
  rows: any[][];
}

export interface ChartSeries {
  key: string;
  values: { x: string; y: number }[];
}

const NOT_SET = 'Not set';

const formatLabel = (value: any, visType: string): string => {
  if (value === null || value === undefined || value === '') {
    return NOT_SET;
  }
  // Line charts are time series bucketed by month (e.g. Shipment Trend).
  if (visType === 'line' && moment(value, moment.ISO_8601, true).isValid()) {
    return moment(value).format('MMM YYYY');
  }
  return String(value);
};

/**
 * Adapts a {label, value...} chart response to the series format the shared
 * Bar/Line/Area visualisations hand to @shipsy/chartify: `[{ key, values: [{ x, y }] }]`.
 * The first column is the label axis; every further column is one series named after
 * its column, so a stacked bar (e.g. vendor x delay bucket) gets one dataset per bucket.
 * Passing the positional rows straight through makes chartify read
 * `chartData[0].values`, which is undefined, and throw while rendering.
 */
export const toSeriesChartData = (data: ReportChartData, visType: string): ChartSeries[] => {
  const valueColumns = data.columns.length > 1 ? data.columns.slice(1) : [{ id: 'count', name: 'Count' }];
  return valueColumns.map((column, index) => ({
    key: column.name || 'Count',
    values: data.rows.map((row) => ({ x: formatLabel(row[0], visType), y: Number(row[index + 1]) || 0 })),
  }));
};

/**
 * Adapts a {label, value} chart response to the flat `[{ x, y }]` points chartify's
 * PieData reads (it has no series level). Always a fresh array: PieData sorts its
 * input in place, which must not reorder the fetched response rows.
 */
export const toPieChartData = (data: ReportChartData): { x: string; y: number }[] =>
  data.rows.map((row) => ({ x: formatLabel(row[0], 'pie'), y: Number(row[1]) || 0 }));

/** The single value of a scalar (number) card, or null when there isn't a usable one. */
export const scalarValue = (data: ReportChartData): number | null => {
  const cell = data.rows[0] && data.rows[0][0];
  if (cell === null || cell === undefined || cell === '') {
    return null;
  }
  const value = Number(cell);
  return Number.isFinite(value) ? value : null;
};
